/**
 * @file
 * Display information from status.acquia.com as a message
 * on the docs site.
 */

import { DrupalTaxonomyTermProduct } from "@/types"
import { addDays, format } from "date-fns"
import classNames from "classnames"
import Markdown from "react-markdown"
import { Fragment } from "react"
import { AcquiaStatusDialog } from "./AcquiaStatus.client"

type Status = "investigating" | "identified" | "monitoring" | "resolved" | "postmortem" | "degraded_performance" | "operational"
type Impact = "minor" | "major" | "critical" | "none"

interface Component {
  "id": string 
  "name": string 
  "status": string
  "created_at": string
  "updated_at": string
  "position": number
  "description": string | null
  "showcase": boolean
  "start_date": string | null
  "group_id": string | null
  "page_id": string
  "group": boolean
  "only_show_if_degraded": boolean
}

interface AffectedComponent {
  code: string
  name: string
  old_status: Status
  new_status: Status
}

interface IncidentUpdate {
  id: string
  status: Status
  body: string
  incident_id: string
  created_at: string
  updated_at: string
  display_at: string
  affected_components: AffectedComponent[] | null
  custom_tweet: string | null
  tweet_id: string | null
  deliver_notifications: boolean
}

export interface Incident {
  id: string
  name: string
  status: Status
  created_at: string // '2023-12-12T10:20:44.797Z',
  updated_at: string // '2023-12-12T13:39:52.890Z',
  monitoring_at: string | null // '2023-12-12T11:49:19.364Z',
  resolved_at: string | null // '2023-12-12T13:39:52.875Z',
  impact: Impact // 'minor',
  shortlink: string // 'https://stspg.io/jvpv8fjml4sd',
  started_at: string // '2023-12-12T10:20:44.790Z',
  page_id: string // 'c3bdbqpxhzcc',
  incident_updates: IncidentUpdate[]
  components: Component[],
  reminder_intervals: null | string
}

export interface IncidentResponse {
  page: {}
  incidents: Incident[]
}

export const AcquiaStatus = async ({ product }: {
  product?: DrupalTaxonomyTermProduct
}) => {
  if (product && !product.field_statuspage_io_component) {
    return <></>
  }

  try {

    const response = await fetch('https://status.acquia.com/api/v2/incidents.json', {
      next: {
        revalidate: 600
      }
    });
    const allIncidents:IncidentResponse = await response.json()
    const now = new Date
    const incidents = allIncidents.incidents.filter(i => {
      if (product) {
        const productComponentIds = product.field_statuspage_io_component?.map(d => d.id) ?? []
        const componentIds = i.components.filter(c => productComponentIds.indexOf(c.id) !== -1)
        if (componentIds.length === 0) {
          return false
        }
      }

      // Show resolved messages for 24 hours after they're resolved.
      if (i.status == 'resolved' && i.resolved_at) {
        return addDays(i.resolved_at, 1) > now
      }
      if (i.status == 'monitoring' && i.monitoring_at) {
        return addDays(i.monitoring_at, 7) > now
      }

      return true;
    })

    if (incidents.length == 0) {
      return <></>
    }

    return <AcquiaStatusMessage product={product} incidents={incidents} />
  }
  catch (err) {
    console.error(err);
    return <></>
  }
}

export const AcquiaStatusMessage = ({ incidents, product}: {
  incidents: Incident[]
  product?: DrupalTaxonomyTermProduct,
}) => {

  if (incidents.length == 0) {
    return <></>
  }

  const maxImpact = incidents.reduce((p, c) => {
    if (!p) {
      return c
    }
    if (p.impact == "critical") {
      return p
    }
    if (c.impact == "critical") {
      return c
    }
    if (p.impact == "major") {
      return p
    }
    if (c.impact == "major") {
      return c
    }
    if (p.impact == "minor") {
      return p
    }

    return c
  }).impact

  const incidentsByImpact:Record<Incident["impact"], number>  = {
    critical: 0,
    major: 0,
    minor: 0,
    none: 0,
  }

  incidents.forEach(i => incidentsByImpact[i.impact]++)

  const impactKeys = Object.keys(incidentsByImpact).filter(k => incidentsByImpact[k as Incident["impact"]] > 0)
  const lastImpactKey = impactKeys.pop()

  return <div className={classNames(mapImpact(maxImpact), "overflow-hidden")}>
          <div className="container mx-auto py-2 px-4">
            <div className={classNames("flex gap-2 items-center py-2 transition-all max-h-16 duration-300 overflow-hidden")}>
              <p className="m-0">
                {impactKeys.map((k, pos) => (
                  <Fragment key={pos}>{incidentsByImpact[k as Incident["impact"]]} {k == 'none' ? 'other' : k}{impactKeys.length != (pos + 1) ? ', ' : ''}</Fragment>
                ))}
                {impactKeys.length > 0 && <> and </>}
                {incidentsByImpact[lastImpactKey as Incident["impact"]]} {
                  impactKeys.length == 0 && lastImpactKey == 'none' ? '' : 
                  lastImpactKey == 'none' ? 'other' : lastImpactKey
                } incident{incidents.length > 1 ? 's' : ''} detected impacting {product?.name ?? 'Acquia'} services.
              </p>
              <AcquiaStatusDialog incidents={incidents} maxImpact={maxImpact}>
                <AcquiaStatusTable incidents={incidents} />
              </AcquiaStatusDialog>
            </div>
          </div>
        </div>
}

export const AcquiaStatusTable = ({incidents}: {
  incidents: Incident[]
}) => {
  return <table className="relative overflow-y-auto max-h-[80vh] block">
  <thead>
    <tr className="sticky top-0 bg-white shadow border-b border-gray-600 text-lg text-left font-display">
      <th className="px-4 py-3">Services</th>
      <th className="px-4 py-3">Incident</th>
    </tr>
  </thead>
  <tbody className="align-top">
    {incidents.map(incident => (
      <tr key={incident.id} className="border-b border-gray-600">
        <td className="p-4">
          <ul className="flex flex-col gap-2.5">
            {incident.components.map(component => (
              <li key={component.id} className="font-medium whitespace-nowrap">{component.name}</li>
            ))}
          </ul>
          <p>
            <span className={classNames(statusBgColor(incident), 'rounded p-1 text-sm font-medium')}>{capitalizeFirstLetter(incident.status)}</span>
          </p>
        </td>
        <td className="p-4">
            <h2 className="flex gap-2 items-center">
              <span className="font-display font-medium">{format(incident.created_at, 'PPPPpppp')}</span>
              {incident.impact != 'none' && (
                <span className={classNames(pillBgColor(incident), "rounded px-1 py-0.5 text-sm")}>{incident.impact}</span>
              )}
            </h2>
            <p className="text-gray-800">{incident.name}</p>
            {incident.status == 'resolved' && incident.resolved_at !== null && (
              <p className="text-gray-800">Resolved: {format(incident.resolved_at, 'PPPPpppp')}</p>
            )}
            <p className="font-medium">Last update: {format(incident.incident_updates[0].created_at, 'PPPPpppp')}</p>
            <blockquote className="pl-4 border-l-8 border-gray-400 text-gray-800">
              <Markdown>{incident.incident_updates[0].body}</Markdown>
            </blockquote>
            <p><a href={incident.shortlink} target="_blank" className="text-blue-600 hover:underline visited:text-pink-800">View timeline{!incident.resolved_at && <> and subscribe to updates</>}</a></p>
        </td>
      </tr>
    ))}
  </tbody>
</table>
}

function mapImpact(impact: Incident["impact"]) {
  switch (impact) {
    case 'critical':
      return "bg-orange-300"
    case 'major':
      return "bg-yellow-300"
    case 'minor':
      return "border border-yellow-600 bg-yellow-300/30"
    default: 
      return "border border-teal-600"
  }
}

function buttonBgColor(impact: Incident["impact"]) {
  if (impact == "minor") {
    return "bg-yellow-600"
  }
  if (impact == "major") {
    return "bg-orange-600"
  }
  if (impact == "critical") {
    return "bg-pink-800";
  }
  return "bg-teal-600"
}

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function statusBgColor(incident: Incident) {
  if (incident.status == "identified") {
    return "bg-pink-600"
  }
  if (incident.status == "investigating") {
    return "bg-orange-600"
  }
  if (incident.status == "monitoring") {
    return "bg-yellow-600"
  }
  if (incident.status == "postmortem") {
    return "bg-teal-600"
  }
  if (incident.status == "resolved") {
    return "bg-teal-600"
  }
  return "bg-teal-600"
}

function pillBgColor(incident: Incident) {
  if (incident.impact == "minor") {
    return "bg-yellow-600"
  }
  if (incident.impact == "major") {
    return "bg-orange-600 text-white"
  }
  if (incident.impact == "critical") {
    return "bg-pink-800 text-white";
  }
  return "bg-yellow-600"
}